import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Database, Clock, BarChart3, Code, Copy, Download, History, Info } from 'lucide-react';

import duckdbService from '../services/duckdbService';
import exportService from '../services/exportService';
import usageService from '../services/usageService';

const SQLQueryInterface = ({ selectedFile, onFileSelect, mode, isAuthenticated, onRequireAuth, onUsageUpdate }) => {
  const [sqlQuery, setSqlQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [queryHistory, setQueryHistory] = useState([]);
  const [showSchema, setShowSchema] = useState(false);

  useEffect(() => {
    const savedHistory = localStorage.getItem('sqlQueryHistory');
    if (savedHistory) {
      try {
        setQueryHistory(JSON.parse(savedHistory));
      } catch (error) {
        console.error('Failed to load query history:', error);
      }
    }
  }, []);

  const saveQueryHistory = (newHistory) => {
    // Custom serializer to handle BigInt values
    const serializer = (key, value) => {
      if (typeof value === 'bigint') {
        return value.toString();
      }
      return value;
    };
    localStorage.setItem('sqlQueryHistory', JSON.stringify(newHistory, serializer));
  };

  const handleExecuteQuery = useCallback(async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    if (!sqlQuery.trim()) {
      setError('Please enter a SQL query');
      return;
    }

    setLoading(true);
    setError(null);
    const startTime = Date.now();

    try {
      if (mode !== 'demo') {
        if (!isAuthenticated) {
          setError('Create a free account to upload and query your own files.');
          if (onRequireAuth) onRequireAuth();
          setLoading(false);
          return;
        }
        await usageService.recordQuery();
        if (onUsageUpdate) onUsageUpdate();
      }

      const datasetInfo = duckdbService.getDatasetInfo(selectedFile.file_id);
      if (!datasetInfo) {
        throw new Error('Dataset not found. Please upload the file again.');
      }

      const queryResult = await duckdbService.executeQuery(sqlQuery.trim(), selectedFile.file_id);

      const endTime = Date.now();
      const clientTime = endTime - startTime;

      const result = {
        ...queryResult,
        query: sqlQuery.trim(),
        timestamp: new Date().toISOString(),
        clientTime,
        file_id: selectedFile.file_id,
        filename: selectedFile.filename
      };

      setResults(result);

      const newHistory = [result, ...queryHistory.slice(0, 19)];
      setQueryHistory(newHistory);
      saveQueryHistory(newHistory);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const message = detail || (error?.message === 'auth_required'
        ? 'Create a free account to upload and query your own files.'
        : error?.message) || 'Query execution failed';
      setError(message);
      console.error('Query error:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedFile, sqlQuery, queryHistory, mode, isAuthenticated, onRequireAuth, onUsageUpdate]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault();
        if (sqlQuery.trim() && selectedFile && !loading) {
          handleExecuteQuery(event);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sqlQuery, selectedFile, loading, handleExecuteQuery]);

  const copyQueryToClipboard = async (query) => {
    try {
      await navigator.clipboard.writeText(query);
    } catch (error) {
      console.error('Failed to copy query:', error);
    }
  };

  const getSampleQueries = () => {
    if (!selectedFile || !selectedFile.columns) return [];

    const tableName = `user_data_${selectedFile.file_id.replace(/-/g, '_')}`;
    const columns = selectedFile.columns;
    const firstCol = columns[0];
    const secondCol = columns.length > 1 ? columns[1] : columns[0];

    return [
      `-- Basic selection\nSELECT * FROM ${tableName} LIMIT 10;`,
      `-- Count total rows\nSELECT COUNT(*) as total_rows FROM ${tableName};`,
      `-- Group by and count\nSELECT ${firstCol}, COUNT(*) as count \nFROM ${tableName} \nGROUP BY ${firstCol} \nORDER BY count DESC \nLIMIT 10;`,
      columns.length > 1 ? `-- Filter and select multiple columns\nSELECT ${columns.slice(0, 3).join(', ')} \nFROM ${tableName} \nWHERE ${firstCol} IS NOT NULL \nLIMIT 20;` : null,
      `-- Order by descending\nSELECT * FROM ${tableName} \nORDER BY ${firstCol} DESC \nLIMIT 5;`,
      columns.length > 1 ? `-- Basic aggregation\nSELECT \n  COUNT(*) as total_count,\n  COUNT(DISTINCT ${firstCol}) as unique_${firstCol}\nFROM ${tableName};` : null,
      `-- Find unique values\nSELECT DISTINCT ${firstCol} \nFROM ${tableName} \nORDER BY ${firstCol};`,
      columns.length > 1 ? `-- Cross-tabulation\nSELECT ${firstCol}, ${secondCol}, COUNT(*) as count\nFROM ${tableName}\nGROUP BY ${firstCol}, ${secondCol}\nORDER BY count DESC;` : null
    ].filter(Boolean);
  };

  if (!selectedFile) {
    return (
      <div className="flex items-center justify-center h-96">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 bg-sand-100 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-[rgba(32,30,29,0.12)]">
            <Database className="w-8 h-8 text-sand-600" />
          </div>
          <h3 className="text-xl font-semibold text-ink mb-2">No File Selected</h3>
          <p className="text-sand-700">Please upload or select a file to start writing SQL queries</p>
        </motion.div>
      </div>
    );
  }

  const tableName = `user_data_${selectedFile.file_id.replace(/-/g, '_')}`;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-sand-100 rounded-lg border border-[rgba(32,30,29,0.12)]">
              <Code className="w-5 h-5 text-ink" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-ink">SQL Query Editor</h2>
              <p className="text-sand-700">Write and execute custom SQL queries on {selectedFile.filename}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowSchema(!showSchema)}
              className="btn-secondary text-sm"
            >
              <Info className="w-4 h-4 mr-2" />
              {showSchema ? 'Hide' : 'Show'} Schema
            </motion.button>
          </div>
        </div>

        <div className="mt-4 p-3 bg-sage-100 border border-sage-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-sage-700" />
              <span className="text-sm font-medium text-sage-900">Table Name:</span>
              <code className="text-sm font-mono bg-white text-ink px-2 py-1 rounded border border-[rgba(32,30,29,0.12)]">
                {tableName}
              </code>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => copyQueryToClipboard(tableName)}
              className="flex items-center gap-1 px-3 py-1 bg-bill-500 hover:bg-bill-600 text-white text-xs rounded-md transition-colors"
            >
              <Copy className="w-3 h-3" />
              Copy
            </motion.button>
          </div>
          <p className="text-xs text-sage-700 mt-1">
            Use this table name in your SQL queries: <code className="font-mono">SELECT * FROM {tableName};</code>
          </p>
        </div>

        <AnimatePresence>
          {showSchema && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 p-4 bg-sand-100 rounded-lg border border-[rgba(32,30,29,0.12)]"
            >
              <h4 className="font-medium text-ink mb-2">Available Columns:</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {selectedFile.columns.map((column) => (
                  <div
                    key={column}
                    className="flex items-center justify-between group"
                  >
                    <span className="px-2 py-1 bg-white text-sand-700 text-sm rounded font-mono flex-1 border border-[rgba(32,30,29,0.12)]">
                      {column}
                    </span>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => copyQueryToClipboard(column)}
                      className="ml-1 p-1 opacity-0 group-hover:opacity-100 transition-opacity text-sand-600 hover:text-ink"
                      title={`Copy column name: ${column}`}
                    >
                      <Copy className="w-3 h-3" />
                    </motion.button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-ink">Query Editor</h3>
          <div className="flex items-center gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => copyQueryToClipboard(sqlQuery)}
              disabled={!sqlQuery.trim()}
              className="p-2 bg-white hover:bg-sand-100 text-sand-600 hover:text-ink rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-[rgba(32,30,29,0.12)]"
            >
              <Copy className="w-4 h-4" />
            </motion.button>
          </div>
        </div>

        <form onSubmit={handleExecuteQuery} className="space-y-4">
          <div className="relative">
            <textarea
              value={sqlQuery}
              onChange={(e) => setSqlQuery(e.target.value)}
              placeholder={`Enter your SQL query here...

Examples:
• SELECT * FROM ${tableName} LIMIT 10;
• SELECT COUNT(*) FROM ${tableName};
• SELECT column_name, COUNT(*) as count FROM ${tableName} GROUP BY column_name;

Tip: Press Ctrl+Enter (Cmd+Enter on Mac) to execute your query`}
              className="input-field min-h-[200px] resize-y font-mono text-sm bg-ink text-paper placeholder-sand-400 border-[rgba(245,234,216,0.18)] focus:border-bill-500"
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === 'Tab') {
                  e.preventDefault();
                  const start = e.target.selectionStart;
                  const end = e.target.selectionEnd;
                  setSqlQuery(sqlQuery.substring(0, start) + '  ' + sqlQuery.substring(end));
                  setTimeout(() => {
                    e.target.selectionStart = e.target.selectionEnd = start + 2;
                  }, 0);
                }
              }}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-sand-600">
              <span className="hidden sm:inline">Press Ctrl+Enter (Cmd+Enter on Mac) to execute • </span>
              <span>Use Tab for indentation</span>
            </div>
            <motion.button
              type="submit"
              disabled={loading || !sqlQuery.trim()}
              className="btn-primary flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Play className="w-4 h-4" />
              {loading ? 'Executing...' : 'Execute Query'}
            </motion.button>
          </div>
        </form>

        <div className="mt-6">
          <h4 className="text-sm font-medium text-sand-600 mb-3">Sample Queries:</h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {getSampleQueries().map((sampleQuery, index) => (
              <motion.button
                key={index}
                onClick={() => setSqlQuery(sampleQuery)}
                className="text-left p-4 bg-ink hover:bg-sand-900 text-paper text-sm rounded-lg transition-colors border border-[rgba(32,30,29,0.12)] hover:border-[rgba(32,30,29,0.2)]"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed">{sampleQuery}</pre>
              </motion.button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Error Display */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="card border-red-200 bg-red-50"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Info className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-red-700">Query Error</h4>
                <p className="text-sm text-red-600 mt-1">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-500 hover:text-red-700 transition-colors"
              >
                ×
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="card text-center">
                <Clock className="w-8 h-8 text-bill-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-ink tnum">{results.execution_time_ms || 0}ms</div>
                <div className="text-sm text-sand-600">Execution Time</div>
              </div>
              <div className="card text-center">
                <Database className="w-8 h-8 text-sage-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-ink tnum">{results.row_count || 0}</div>
                <div className="text-sm text-sand-600">Rows Returned</div>
              </div>
              <div className="card text-center">
                <BarChart3 className="w-8 h-8 text-bill-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-ink tnum">
                  {results.result && results.result.length > 0 ? Object.keys(results.result[0]).length : 0}
                </div>
                <div className="text-sm text-sand-600">Columns</div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-ink">Executed Query</h3>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => copyQueryToClipboard(results.sql)}
                  className="p-2 bg-white hover:bg-sand-100 text-sand-600 hover:text-ink rounded-lg transition-colors border border-[rgba(32,30,29,0.12)]"
                >
                  <Copy className="w-4 h-4" />
                </motion.button>
              </div>
              <pre className="bg-ink p-4 rounded-lg overflow-x-auto text-sm font-mono text-paper whitespace-pre-wrap border border-[rgba(32,30,29,0.12)]">
                {results.sql}
              </pre>
            </div>

            {results.result && results.result.length > 0 && (
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-ink">Results</h3>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn-secondary text-sm"
                    onClick={() => exportService.exportToCSV(results.result, `sql_query_${Date.now()}`)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </motion.button>
                </div>
                <div className="table-container">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-sand-100">
                        <tr>
                          {Object.keys(results.result[0]).map((column) => (
                            <th key={column} className="px-4 py-3 text-left text-sm font-medium text-sand-600">
                              {column}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[rgba(32,30,29,0.08)]">
                        {results.result.slice(0, 100).map((row, index) => (
                          <tr key={index} className="hover:bg-sand-100">
                            {Object.values(row).map((value, cellIndex) => (
                              <td key={cellIndex} className="px-4 py-3 text-sm text-sand-700 tnum">
                                {value === null || value === undefined ? (
                                  <span className="text-sand-500 italic">null</span>
                                ) : (
                                  <span className="break-words">
                                    {typeof value === 'bigint' ? value.toString() : String(value)}
                                  </span>
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {results.result.length > 100 && (
                    <div className="px-4 py-3 bg-sand-100 text-sm text-sand-600 text-center border-t border-[rgba(32,30,29,0.08)]">
                      Showing first 100 of {results.result.length} results
                    </div>
                  )}
                </div>
              </div>
            )}

            {results.result && results.result.length === 0 && (
              <div className="card text-center py-8">
                <Database className="w-12 h-12 text-sand-500 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-ink mb-2">No Results</h3>
                <p className="text-sand-600">Your query executed successfully but returned no results.</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {queryHistory.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <History className="w-5 h-5 text-ink" />
              <h3 className="text-lg font-semibold text-ink">Query History</h3>
            </div>
            <button
              onClick={() => {
                setQueryHistory([]);
                localStorage.removeItem('sqlQueryHistory');
              }}
              className="text-sm text-sand-600 hover:text-ink transition-colors"
            >
              Clear History
            </button>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
            {queryHistory.slice(0, 10).map((historyItem, index) => (
              <motion.div
                key={index}
                className="p-3 bg-sand-100 rounded-lg hover:bg-sand-200 transition-colors cursor-pointer border border-[rgba(32,30,29,0.08)]"
                onClick={() => setSqlQuery(historyItem.sql)}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-sand-600 tnum">
                    {new Date(historyItem.timestamp).toLocaleString()}
                  </span>
                  <div className="flex items-center gap-3 text-xs text-sand-600 tnum">
                    <span>{historyItem.row_count} rows</span>
                    <span>{historyItem.execution_time_ms}ms</span>
                    <span className="text-bill-600 font-medium">{historyItem.filename}</span>
                  </div>
                </div>
                <code className="text-sm text-paper block break-words bg-ink p-2 rounded border border-[rgba(32,30,29,0.12)]">
                  {historyItem.sql}
                </code>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default SQLQueryInterface;
